import {Box, Button, Grid, InputLabel, TextField, useTheme } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { CustomGridToolbar } from "../../../utils/dataGridCustom";
import { tokens } from "../../../theme";
import Modal from "@mui/material/Modal";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { toast } from "react-toastify";
import {getCenterAllStudents, updateRegisteredStudent } from "../../../services/center-manager-services/getStudentData";
import Loader from "../../global/Loader";
import { pageSizeOptions, pageSize, page } from "../../../utils/paginationConfig";
import { saveAs } from "file-saver";
import { escapeCSVField } from "../../../utils/utils";

const ViewAllStudents = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [centerCode, setCenterCode] = useState("");
  const [paginationModel, setPaginationModel] = useState({page: page, pageSize: pageSize});
  const [rowCount, setRowCount] = useState(0);
  const [selectedData, setSelectedData] = useState({});

  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const headerData = ["Name", "Phone Number", "Guardian Phone Number", "Address"];

  const initialValues = {
    email: "",
    studentName: "",
    phoneNumber: "",
    parentPhoneNumber: "",
    address: "",
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Invalid email format: Enter a valid specific email address"
      )
      .trim(),
    phoneNumber: Yup.string()
      .matches(/^0\d{9}$/, "Invalid phone number. 0xxxxxxxxx")
      .trim(),
    parentPhoneNumber: Yup.string()
      .matches(/^0\d{9}$/, "Invalid phone number. 0xxxxxxxxx")
      .trim(),
  });

  useEffect(() => {
    const centerId = localStorage.getItem("CenterCode");
    setCenterCode(centerId);

    const fetchData = async () => {
      await handleTableData(centerId, page, pageSize);
    };
    fetchData();
  }, []);


  const handleUpdate = (id) => {
    const selectedData = data.find((element) => element.studentCode === id);
    setSelectedData(selectedData);
    handleOpen();
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    textAlign: "center",
  };

  const columns = [
    {
      field: "studentName",
      headerName: headerData[0],
      flex: 4,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Box style={{ textTransform: "uppercase" }}>{params.value}</Box>
      ),
    },
    {
      field: "phoneNumber",
      headerName: headerData[1],
      flex: 2,
    },
    {
      field: "parentPhoneNumber",
      headerName: headerData[2],
      flex: 2,
      type: "number",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "address",
      headerName: headerData[3],
      flex: 4,
      type: "number",
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Box style={{ textTransform: "uppercase" }}>{params.value}</Box>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 2,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Box>
          <Button
            onClick={() => handleUpdate(params.id)}
            variant="contained"
            style={{ backgroundColor: "#4cceac", color: "black" }}
          >
            Update
          </Button>
        </Box>
      ),
    },
  ];

  const handleTableData = async (centerCode, page = paginationModel.page, limit = paginationModel.pageSize) => {
    setIsLoading(true);
    setPaginationModel({page: page, pageSize: limit});

    try {
      const obj = {page, limit};
      const response = await getCenterAllStudents(centerCode, obj);
      setData(response.data.data);
      setRowCount(response.data.totalCount ?? 0);
    } catch (error) {
      console.log(error);
      toast.error("Error fetching data");
    }
    setIsLoading(false);
  };

  const handlePaginationModelChange = (newModel) => {
    const resetPage = newModel.pageSize !== paginationModel.pageSize;
    const updatedModel = {
      ...newModel,
      page: resetPage ? 0 : newModel.page,
    };

    setPaginationModel(updatedModel);
    handleTableData(centerCode, updatedModel.page, updatedModel.pageSize);
  };

  const handleExportCSV = async () => {
    try {
      const response = await getCenterAllStudents(centerCode, {page: 0, limit: rowCount});

      const data = response.data.data;
      const csvRows = data.map(row =>
        [
          escapeCSVField(row.studentName),
          escapeCSVField(row.phoneNumber),
          escapeCSVField(row.parentPhoneNumber),
          escapeCSVField(row.address),
        ].join(",")
      );

      const csvContent = [headerData.join(","), ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `Register_students.csv`);
    } catch (error) {
      console.error("CSV Export Failed:", error);
      toast.error("Failed to export CSV.");
    }
  };


  return (
    <Box m="0 20px">
      {isLoading ? (
        <Loader />
      ) : (
        <Box
          display="grid"
          height="78vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              fontSize: "15px",
            },
            "& .name-column--cell": {
              color: colors.primary[100],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              border: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.redAccent[100]} !important`,
            },
          }}
        >
          <DataGrid
            rows={data}
            getRowId={(row) => row.studentCode}
            columns={columns}
            paginationMode="server"
            rowCount={rowCount}
            pageSizeOptions={pageSizeOptions}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            loading={isLoading}
            components={{ Toolbar: CustomGridToolbar }}
            componentsProps={{
              toolbar: {
                onExport: handleExportCSV,
              },
            }}
          />
        </Box>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              Object.keys(values).forEach((fieldName) => {
                if (
                  values[fieldName] === "" &&
                  selectedData[fieldName] !== undefined
                ) {
                  values[fieldName] = selectedData[fieldName];
                }
              });
              try {
                const studentCode = selectedData.studentCode;
                const response = await updateRegisteredStudent(
                  studentCode,
                  values
                );
                if (response.data.sql_msg === "success") {
                  toast.success("Student updated successfully!");
                } else {
                  toast.error(
                    "An error occurred while updating the student. Please try again."
                  );
                }
              } catch (error) {
                toast.error("Error submitting form:", error);
              } finally {
                handleClose(); // Close the modal after submission
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              handleSubmit,
              isValid,
              errors,
              touched,
              handleBlur,
              handleChange,
              resetForm,
            }) => (
              <Box noValidate sx={{ mt: 1 }}>
                <form onSubmit={handleSubmit}>
                  <InputLabel htmlFor="email">Email address</InputLabel>
                  <TextField
                    margin="normal"
                    fullWidth
                    variant="filled"
                    id="email"
                    label={selectedData.email}
                    name="email"
                    autoComplete="none"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email.toLocaleLowerCase()}
                    error={!!touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                  <InputLabel margin="0px" htmlFor="studentName">
                    Name
                  </InputLabel>
                  <TextField
                    margin="normal"
                    variant="filled"
                    fullWidth
                    name="studentName"
                    label={selectedData.studentName}
                    type="text"
                    id="studentName"
                    autoComplete="none"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    error={!!touched.studentName && !!errors.studentName}
                    helperText={touched.studentName && errors.studentName}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <InputLabel htmlFor="phoneNumber">
                        Phone Number
                      </InputLabel>
                      <TextField
                        margin="normal"
                        variant="filled"
                        fullWidth
                        name="phoneNumber"
                        label={selectedData.phoneNumber}
                        type="text"
                        id="phoneNumber"
                        autoComplete="none"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.phone}
                        error={!!touched.phoneNumber && !!errors.phoneNumber}
                        helperText={touched.phoneNumber && errors.phoneNumber}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputLabel htmlFor="parentPhoneNumber">
                        Guardian Phone Number
                      </InputLabel>
                      <TextField
                        margin="normal"
                        variant="filled"
                        fullWidth
                        name="parentPhoneNumber"
                        label={selectedData.parentPhoneNumber}
                        type="text"
                        id="parentPhoneNumber"
                        autoComplete="none"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.parentPhoneNumber}
                        error={
                          !!touched.parentPhoneNumber &&
                          !!errors.parentPhoneNumber
                        }
                        helperText={
                          touched.parentPhoneNumber && errors.parentPhoneNumber
                        }
                      />
                    </Grid>
                  </Grid>
                  <InputLabel htmlFor="address">Address</InputLabel>
                  <TextField
                    margin="normal"
                    variant="filled"
                    fullWidth
                    name="address"
                    label={selectedData.address}
                    type="address"
                    id="address"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.address}
                    error={!!touched.address && !!errors.address}
                    helperText={touched.address && errors.address}
                  />
                  <Box display="flex" justifyContent="center" marginTop="10px">
                    <Button
                      variant="contained"
                      type="submit"
                      style={{
                        backgroundColor: "red",
                        fontSize: "15px",
                        margin: "10px",
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      style={{
                        backgroundColor: "white",
                        fontSize: "15px",
                        margin: "10px",
                        color: "red",
                      }}
                      onClick={resetForm}
                    >
                      Reset
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Formik>
        </Box>
      </Modal>
    </Box>
  );
};

export default ViewAllStudents;
/**/
